import React, { useCallback, useEffect, useRef } from "react";

export function defaultBlockStyle(type) {
  switch (type) {
    case "heading":
      return {
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 32,
        fontWeight: 600,
        lineHeight: 1.12,
        letterSpacing: "-0.02em",
        color: "#111827",
        textAlign: "left",
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 0,
        paddingRight: 0,
        backgroundColor: "transparent",
        borderRadius: 0,
        borderWidth: 0,
        borderColor: "transparent"
      };
    case "text":
      return {
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1.65,
        letterSpacing: "0.01em",
        color: "#374151",
        textAlign: "left",
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 0,
        paddingRight: 0,
        backgroundColor: "transparent",
        borderRadius: 0,
        borderWidth: 0,
        borderColor: "transparent"
      };
    case "button":
      return {
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 14,
        fontWeight: 600,
        textAlign: "left",
        backgroundColor: "#0078d4",
        color: "#ffffff",
        borderRadius: 8,
        paddingX: 24,
        paddingY: 12,
        fullWidth: false,
        borderWidth: 0,
        borderColor: "#0078d4"
      };
    case "footer":
      return {
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.5,
        color: "#6b7280",
        textAlign: "center",
        paddingTop: 16,
        paddingBottom: 8
      };
    case "social":
      return {
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.4,
        color: "#6b7280",
        textAlign: "center",
        paddingTop: 8,
        paddingBottom: 8
      };
    case "columns":
      return {
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: "transparent",
        borderRadius: 0
      };
    default:
      return { paddingTop: 8, paddingBottom: 8 };
  }
}

export function mergeDefaultStyle(block = {}) {
  const base = defaultBlockStyle(block.type || "text");
  const prev = block.style && typeof block.style === "object" ? block.style : {};
  return { ...base, ...prev };
}

export function previewScaleForDevice(device) {
  if (device === "tablet") return 0.94;
  if (device === "mobile") return 0.88;
  return 1;
}

export function blockOuterStyle(style, device) {
  const s = style || {};
  const scale = previewScaleForDevice(device);
  const pt = Number(s.paddingTop ?? 8);
  const pb = Number(s.paddingBottom ?? 8);
  const pl = Number(s.paddingLeft ?? 0);
  const pr = Number(s.paddingRight ?? 0);
  return {
    paddingTop: pt * scale,
    paddingBottom: pb * scale,
    paddingLeft: pl,
    paddingRight: pr,
    backgroundColor: s.backgroundColor || "transparent",
    borderRadius: Number(s.borderRadius ?? 0)
  };
}

export function blockInnerTypographyStyle(style, device, isHeading) {
  const s = style || {};
  const scale = previewScaleForDevice(device);
  const rawSize = Number(s.fontSize ?? (isHeading ? 32 : 16));
  return {
    fontFamily: s.fontFamily,
    fontSize: Math.max(11, rawSize * scale),
    fontWeight: s.fontWeight,
    lineHeight: s.lineHeight,
    letterSpacing: s.letterSpacing,
    color: s.color,
    textAlign: s.textAlign || "left"
  };
}

export function buttonPreviewStyle(block, device) {
  const s = mergeDefaultStyle(block);
  const scale = previewScaleForDevice(device);
  return {
    fontFamily: s.fontFamily,
    fontSize: Math.max(11, Number(s.fontSize || 14) * scale),
    fontWeight: s.fontWeight,
    backgroundColor: s.backgroundColor,
    color: s.color,
    borderRadius: Number(s.borderRadius ?? 8),
    padding: `${Number(s.paddingY ?? 12) * scale}px ${Number(s.paddingX ?? 24)}px`,
    borderWidth: Number(s.borderWidth ?? 0) ? `${Number(s.borderWidth)}px` : 0,
    borderStyle: Number(s.borderWidth ?? 0) ? "solid" : "none",
    borderColor: s.borderColor || s.backgroundColor,
    width: s.fullWidth ? "100%" : "auto",
    display: "inline-block",
    textAlign: "center",
    cursor: "default",
    boxSizing: "border-box"
  };
}

export function plainTextFromHtml(html) {
  if (typeof window === "undefined" || !html) return "";
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || "").replace(/\u00a0/g, " ").trim();
}

function stripHtml(html) {
  return plainTextFromHtml(html);
}

function exec(cmd, value = null) {
  try {
    document.execCommand(cmd, false, value);
  } catch (_e) {
    /* ignore */
  }
}

const MERGE_TAGS = ["{{firstName}}", "{{lastName}}", "{{senderName}}", "{{companyName}}", "{{unsubscribeUrl}}"];

export function RichTextBlockEditor({ html, plainFallback, disabled, onChange, placeholder }) {
  const ref = useRef(null);
  const lastHtml = useRef(html || "");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = html || (plainFallback ? `<p>${plainFallback.replace(/\n/g, "<br/>")}</p>` : `<p>${placeholder || ""}</p>`);
    if (incoming !== lastHtml.current && incoming !== el.innerHTML) {
      el.innerHTML = incoming;
      lastHtml.current = incoming;
    }
  }, [html, plainFallback, placeholder]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el || disabled) return;
    const next = el.innerHTML;
    lastHtml.current = next;
    onChange({ html: next, plain: stripHtml(next) });
  }, [disabled, onChange]);

  const fmt = (fn) => (e) => {
    e.preventDefault();
    if (disabled) return;
    fn();
    requestAnimationFrame(() => emit());
  };

  const toolBtn =
    "h-8 px-2 rounded-md border border-pearl-200 bg-white text-[11px] font-semibold text-slateLux-800 hover:bg-pearl-50 disabled:opacity-40 transition";

  return (
    <div className="rounded-xl2 border border-pearl-200 overflow-hidden bg-white shadow-inner">
      <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-pearl-200 bg-gradient-to-b from-pearl-50/90 to-white">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slateLux-400 self-center mr-1">Format</span>
        <button type="button" className={toolBtn} disabled={disabled} onMouseDown={fmt(() => exec("bold"))}>
          B
        </button>
        <button type="button" className={`${toolBtn} italic`} disabled={disabled} onMouseDown={fmt(() => exec("italic"))}>
          I
        </button>
        <button type="button" className={toolBtn} disabled={disabled} onMouseDown={fmt(() => exec("underline"))}>
          U
        </button>
        <button type="button" className={toolBtn} disabled={disabled} onMouseDown={fmt(() => exec("insertUnorderedList"))}>
          • List
        </button>
        <button type="button" className={toolBtn} disabled={disabled} onMouseDown={fmt(() => exec("insertOrderedList"))}>
          1. List
        </button>
        <button
          type="button"
          className={toolBtn}
          disabled={disabled}
          onMouseDown={fmt(() => {
            const url = window.prompt("Link URL", "https://");
            if (url) exec("createLink", url);
          })}
        >
          Link
        </button>
        <button type="button" className={toolBtn} disabled={disabled} onMouseDown={fmt(() => exec("removeFormat"))}>
          Clear
        </button>
        <div className="w-px h-6 bg-pearl-200 self-center mx-1" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-slateLux-400 self-center mr-1">Merge</span>
        {MERGE_TAGS.slice(0, 3).map((tag) => (
          <button
            key={tag}
            type="button"
            className="h-8 px-1.5 rounded-md border border-accent-600/20 bg-accent-600/5 text-[10px] font-mono text-accent-700 hover:bg-accent-600/10"
            disabled={disabled}
            onMouseDown={fmt(() => exec("insertText", tag))}
          >
            {tag}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="min-h-[14rem] px-3 py-3 text-sm text-slateLux-800 focus:outline-none max-w-none [&_a]:text-accent-600 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder || ""}
        onInput={emit}
        onBlur={emit}
        style={{ whiteSpace: "pre-wrap" }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <details open className="rounded-xl2 border border-pearl-200 bg-pearl-50/50 overflow-hidden">
      <summary className="cursor-pointer select-none px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slateLux-500 bg-pearl-50 border-b border-pearl-200">
        {title}
      </summary>
      <div className="p-3 space-y-3">{children}</div>
    </details>
  );
}

function LabeledRange({ label, value, min, max, step, onChange }) {
  return (
    <label className="block">
      <div className="flex justify-between text-[10px] text-slateLux-500 mb-1">
        <span>{label}</span>
        <span className="font-mono text-slateLux-700">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-accent-600" />
    </label>
  );
}

export function BlockDesignInspector({ block, disabled, onPatchStyle }) {
  if (!block) return null;
  const style = mergeDefaultStyle(block);
  const patch = (partial) => onPatchStyle(partial);

  if (block.type === "button") {
    return (
      <div className="space-y-2">
        <Section title="Colors">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slateLux-500">
              Fill
              <input type="color" className="mt-1 h-9 w-full rounded border border-pearl-200" value={style.backgroundColor} disabled={disabled} onChange={(e) => patch({ backgroundColor: e.target.value })} />
            </label>
            <label className="text-[10px] text-slateLux-500">
              Text
              <input type="color" className="mt-1 h-9 w-full rounded border border-pearl-200" value={style.color} disabled={disabled} onChange={(e) => patch({ color: e.target.value })} />
            </label>
            <label className="text-[10px] text-slateLux-500 col-span-2">
              Border (optional)
              <div className="flex gap-2 mt-1">
                <input type="color" className="h-9 flex-1 rounded border border-pearl-200" value={style.borderColor} disabled={disabled} onChange={(e) => patch({ borderColor: e.target.value })} />
                <input
                  type="number"
                  min={0}
                  max={6}
                  className="h-9 w-16 rounded border border-pearl-200 px-2 text-xs"
                  value={style.borderWidth}
                  disabled={disabled}
                  onChange={(e) => patch({ borderWidth: Number(e.target.value) })}
                />
              </div>
            </label>
          </div>
        </Section>
        <Section title="Layout">
          <LabeledRange label="Corner radius" value={Number(style.borderRadius)} min={0} max={40} step={1} onChange={(v) => patch({ borderRadius: v })} />
          <LabeledRange label="Horizontal padding" value={Number(style.paddingX)} min={8} max={48} step={1} onChange={(v) => patch({ paddingX: v })} />
          <LabeledRange label="Vertical padding" value={Number(style.paddingY)} min={4} max={32} step={1} onChange={(v) => patch({ paddingY: v })} />
          <label className="flex items-center gap-2 text-xs text-slateLux-700">
            <input type="checkbox" checked={Boolean(style.fullWidth)} disabled={disabled} onChange={(e) => patch({ fullWidth: e.target.checked })} />
            Full width button
          </label>
          <label className="block text-[10px] text-slateLux-500">Alignment</label>
          <div className="flex gap-1">
            {["left", "center", "right"].map((a) => (
              <button
                key={a}
                type="button"
                disabled={disabled}
                className={`flex-1 h-8 rounded-md border text-[11px] font-semibold capitalize ${style.textAlign === a ? "border-accent-600 bg-accent-600/10 text-accent-700" : "border-pearl-200 bg-white text-slateLux-700 hover:bg-pearl-50"}`}
                onClick={() => patch({ textAlign: a })}
              >
                {a}
              </button>
            ))}
          </div>
        </Section>
      </div>
    );
  }

  if (!["heading", "text", "footer", "social"].includes(block.type)) {
    return <div className="text-xs text-slateLux-500">Select a text block for typography controls.</div>;
  }

  const fonts = [
    { id: "system", label: "System UI", value: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
    { id: "georgia", label: "Georgia (serif)", value: 'Georgia, "Times New Roman", serif' },
    { id: "mono", label: "Monospace", value: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }
  ];

  return (
    <div className="space-y-2">
      <Section title="Typography">
        <label className="block text-[10px] text-slateLux-500">Font</label>
        <select
          className="w-full h-9 rounded-lg border border-pearl-200 px-2 text-xs bg-white"
          disabled={disabled}
          value={fonts.find((f) => f.value === style.fontFamily)?.id || "system"}
          onChange={(e) => {
            const f = fonts.find((x) => x.id === e.target.value);
            if (f) patch({ fontFamily: f.value });
          }}
        >
          {fonts.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <LabeledRange label="Size (px)" value={Number(style.fontSize)} min={block.type === "heading" ? 20 : 12} max={block.type === "heading" ? 52 : 22} step={1} onChange={(v) => patch({ fontSize: v })} />
        <LabeledRange label="Weight" value={Number(style.fontWeight)} min={400} max={800} step={100} onChange={(v) => patch({ fontWeight: v })} />
        <LabeledRange label="Line height" value={Number(style.lineHeight)} min={1} max={2} step={0.05} onChange={(v) => patch({ lineHeight: v })} />
        <label className="block text-[10px] text-slateLux-500">Text color</label>
        <input type="color" className="h-9 w-full rounded border border-pearl-200" value={style.color} disabled={disabled} onChange={(e) => patch({ color: e.target.value })} />
      </Section>
      <Section title="Layout & spacing">
        <label className="block text-[10px] text-slateLux-500">Alignment</label>
        <div className="flex gap-1">
          {["left", "center", "right", "justify"].map((a) => (
            <button
              key={a}
              type="button"
              disabled={disabled}
              className={`flex-1 h-8 rounded-md border text-[10px] font-semibold capitalize ${style.textAlign === a ? "border-accent-600 bg-accent-600/10 text-accent-700" : "border-pearl-200 bg-white text-slateLux-700 hover:bg-pearl-50"}`}
              onClick={() => patch({ textAlign: a })}
            >
              {a === "justify" ? "Justify" : a}
            </button>
          ))}
        </div>
        <LabeledRange label="Space above" value={Number(style.paddingTop)} min={0} max={64} step={1} onChange={(v) => patch({ paddingTop: v })} />
        <LabeledRange label="Space below" value={Number(style.paddingBottom)} min={0} max={64} step={1} onChange={(v) => patch({ paddingBottom: v })} />
        <LabeledRange label="Inset left / right" value={Number(style.paddingLeft)} min={0} max={48} step={1} onChange={(v) => patch({ paddingLeft: v, paddingRight: v })} />
      </Section>
      <Section title="Background">
        <label className="block text-[10px] text-slateLux-500">Section fill</label>
        <div className="flex gap-2">
          <input type="color" className="h-9 flex-1 rounded border border-pearl-200" value={style.backgroundColor === "transparent" ? "#ffffff" : style.backgroundColor} disabled={disabled} onChange={(e) => patch({ backgroundColor: e.target.value })} />
          <button type="button" className="h-9 px-2 text-[11px] border border-pearl-200 rounded-md bg-white hover:bg-pearl-50" disabled={disabled} onClick={() => patch({ backgroundColor: "transparent" })}>
            Clear
          </button>
        </div>
        <LabeledRange label="Corner radius" value={Number(style.borderRadius)} min={0} max={24} step={1} onChange={(v) => patch({ borderRadius: v })} />
      </Section>
    </div>
  );
}
